import { Box, Button, ButtonProps, Skeleton, useTheme } from "@mui/material";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { type RenderPageProps, type SpecialZoomLevel, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import { type BoxRegion } from "components/Document/ReviewInteraction";
import { memo, useCallback, useRef } from "react";

import { useFramedRegion } from "./useFramedRegion";

/**
 * A region of one page to point the reader at.
 *
 * A hint, never a crop. The region comes from the fixed layout of a printed form
 * rather than from anything measured on this document, so a rotated, cropped or
 * photographed scan lands it imprecisely — and those are the documents a reader
 * most needs pointing at. It is drawn as a soft outline over a page that still
 * pans and zooms normally.
 */
export interface DocumentPreviewHighlight {
  /** 1-based page the region is on. */
  page: number;
  region: BoxRegion;
  /** What the outline is pointing at, e.g. "Box 11 · Total quantity". */
  label?: string;
}

/** Shared so a caller with nothing to outline does not hand this a new array each render. */
const EMPTY_HIGHLIGHTS: readonly DocumentPreviewHighlight[] = [];

export interface DocumentPreviewProps {
  fileUrl: string;
  bottomLeftButtonProps?: ButtonProps & {
    text: string;
    toolTipText?: string;
  };
  /**
   * Initial zoom applied once the PDF loads (e.g. `SpecialZoomLevel.PageFit`).
   * Defaults to the viewer's actual-size behavior when omitted.
   */
  defaultScale?: number | SpecialZoomLevel;
  /**
   * Regions to outline. Usually one — the field the reviewer is in — but the
   * region-verification sheet draws the whole map at once.
   */
  highlights?: readonly DocumentPreviewHighlight[];
  /**
   * The region to bring into view: its page, then a zoom that frames it.
   *
   * Separate from `highlights` because drawing and framing are different jobs.
   * Changing it moves the viewer; clearing it leaves the reader wherever they
   * are rather than scrolling them back, since losing your place is worse than a
   * stale outline.
   */
  frame?: DocumentPreviewHighlight | null;
}

/**
 * One region, drawn over the page as a soft rectangle with a tag naming it.
 *
 * Positioned in percentages so it scales with zoom for free and needs to know
 * nothing about the scan's DPI. Never over the page content — the reader has to
 * be able to read what is underneath, which is the whole point of pointing at it.
 */
function RegionOutline({
  region,
  label,
  outlineRef,
}: {
  region: BoxRegion;
  label?: string;
  /** Set on the framed region only, so the viewer can bring it into view. */
  outlineRef?: (element: HTMLDivElement | null) => void;
}) {
  const place = {
    position: "absolute",
    left: `${region.x * 100}%`,
    top: `${region.y * 100}%`,
    pointerEvents: "none",
  } as const;

  return (
    <>
      <Box
        aria-hidden
        ref={outlineRef}
        sx={{
          ...place,
          width: `${region.width * 100}%`,
          height: `${region.height * 100}%`,
          border: 2,
          borderColor: "primary.main",
          borderRadius: 0.5,
          backgroundColor: "primary.main",
          opacity: 0.12,
        }}
      />
      {label != null && (
        <Box
          aria-hidden
          sx={{
            ...place,
            transform: "translateY(-100%)",
            px: 0.75,
            py: 0.25,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            borderRadius: 0.5,
            fontSize: "0.7rem",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>
      )}
    </>
  );
}

/**
 * Memoized because rendering this is expensive and its surroundings re-render
 * constantly.
 *
 * A review screen re-renders its dialog for reasons that have nothing to do
 * with the document — a validation pass finishing, focus moving to another
 * field — and each of those rebuilt the toolbar plugin below and re-rendered
 * the viewer with it, which is slow enough to feel as a delay before a clicked
 * field takes the caret. Skipping the render entirely is the only way out:
 * the plugin cannot be memoized on its own, for the reason given below.
 *
 * The comparison is the default shallow one, so a caller must hand this stable
 * props — `highlights` in particular, which is easy to rebuild by accident.
 */
export const DocumentPreview = memo(function DocumentPreview({
  fileUrl,
  bottomLeftButtonProps,
  defaultScale,
  highlights = EMPTY_HIGHLIGHTS,
  frame,
}: DocumentPreviewProps) {
  // Called on every render, never memoized. `toolbarPlugin()` calls hooks of its
  // own, which makes it a hook: wrapping it in `useMemo` runs those hooks on the
  // first render and skips them on the next, and a hook count that changes
  // between renders corrupts React's hook list — it fails as an unrelated crash
  // in some other component's effect, not as anything pointing here.
  const toolbarPluginInstance = toolbarPlugin();
  const { Toolbar } = toolbarPluginInstance;
  const theme = useTheme();

  // The instance is therefore a new object each render, so an effect must not
  // depend on it — it would re-run on every keystroke that re-renders the pane.
  // The methods on it close over the plugin's own refs, so the latest instance is
  // always the right one to call.
  const pluginRef = useRef(toolbarPluginInstance);
  pluginRef.current = toolbarPluginInstance;

  // Its two controls act on their own schedule, so the sequencing that keeps the
  // framed box on screen is involved enough to read on its own.
  const { viewportRef, attachFramedOutline, frameKey, onDocumentLoad, notePageSize, noteScale } =
    useFramedRegion({
      fileUrl,
      frame,
      zoomTo: useCallback((scale: number) => {
        pluginRef.current.zoomPluginInstance.zoomTo(scale);
      }, []),
      jumpToPage: useCallback((pageIndex: number) => {
        pluginRef.current.pageNavigationPluginInstance.jumpToPage(pageIndex);
      }, []),
    });

  const renderPage = useCallback(
    (props: RenderPageProps) => {
      // The page renders at `scale`, so its own size divided by that is what it
      // measures at scale 1 — the unit the viewer's zoom is expressed in, and the
      // one `regionZoomScale` needs.
      notePageSize(
        props.scale > 0
          ? { width: props.width / props.scale, height: props.height / props.scale }
          : null
      );
      const onThisPage = highlights.filter((h) => h.page - 1 === props.pageIndex);
      return (
        <>
          {props.canvasLayer.children}
          {props.textLayer.children}
          {props.annotationLayer.children}
          {onThisPage.map((h) => {
            const key = `${h.page}:${h.region.x}:${h.region.y}:${h.region.width}:${h.region.height}`;
            const isFramed = frameKey != null && key === frameKey;
            return (
              <RegionOutline
                key={`${key}:${h.label ?? ""}`}
                region={h.region}
                label={h.label}
                outlineRef={isFramed ? attachFramedOutline : undefined}
              />
            );
          })}
        </>
      );
    },
    [highlights, frameKey, attachFramedOutline, notePageSize]
  );

  return (
    <Box
      className="rpv-core__viewer"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          bgcolor: theme.palette.grey50.main,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          padding: "4px",
        }}
      >
        <Toolbar>
          {(props) => {
            const {
              CurrentPageInput,
              GoToNextPage,
              GoToPreviousPage,
              NumberOfPages,
              ShowSearchPopover,
              Zoom,
              ZoomIn,
              ZoomOut,
            } = props;
            return (
              <>
                <div style={{ padding: "0px 2px" }}>
                  <ShowSearchPopover />
                </div>
                <div style={{ padding: "0px 2px" }}>
                  <ZoomOut />
                </div>
                <div style={{ padding: "0px 2px" }}>
                  <Zoom />
                </div>
                <div style={{ padding: "0px 2px" }}>
                  <ZoomIn />
                </div>
                <div style={{ padding: "0px 2px", marginLeft: "auto" }}>
                  <GoToPreviousPage />
                </div>
                <div className="rpv-toolbar__item">
                  <CurrentPageInput />
                </div>
                <span className="rpv-toolbar__label">
                  / <NumberOfPages />
                </span>
                <div style={{ padding: "0px 2px" }}>
                  <GoToNextPage />
                </div>
              </>
            );
          }}
        </Toolbar>
      </Box>
      <Box ref={viewportRef} sx={{ height: "calc(100% - 43px)", position: "relative" }}>
        {fileUrl && (
          <Viewer
            fileUrl={fileUrl}
            defaultScale={defaultScale}
            plugins={[toolbarPluginInstance]}
            onDocumentLoad={onDocumentLoad}
            onZoom={(event) => {
              noteScale(event.scale);
            }}
            renderPage={renderPage}
            renderError={() => <Box sx={{ p: 2 }}>Document preview only displays for PDFs.</Box>}
            renderLoader={() => (
              <Skeleton
                variant="rectangular"
                animation="wave"
                height="100%"
                width="100%"
                sx={{
                  borderRadius: theme.spacing(0.5),
                }}
              />
            )}
          />
        )}
        {bottomLeftButtonProps && (
          <Button
            variant="contained"
            {...bottomLeftButtonProps}
            sx={{
              position: "absolute",
              bottom: 16,
              left: 16,
              zIndex: 1,
              ...bottomLeftButtonProps.sx,
            }}
          >
            {bottomLeftButtonProps.text}
          </Button>
        )}
      </Box>
    </Box>
  );
});
